import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, fileUrl } from "@/lib/api";
import { formatMoney, COUNTRIES, US_STATES } from "@/lib/currency";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";

export default function CheckoutPage() {
  const { itemId } = useParams();
  const nav = useNavigate();
  const { settings } = useSettings();
  const [item, setItem] = useState(null);
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    buyer_name: "", buyer_email: "", buyer_phone: "",
    address_line: "", city: "", postal_code: "",
    state: "", country: "US", notes: "",
  });

  useEffect(() => {
    let cancelled = false;
    api.get(`/storefront/inventory/${itemId}`)
      .then((r) => { if (!cancelled) setItem(r.data); })
      .catch(() => toast.error("piece not found."));
    return () => { cancelled = true; };
  }, [itemId]);

  useEffect(() => {
    if (!item) return;
    let cancelled = false;
    api.post("/storefront/checkout/quote", { item_id: item.id, country: form.country, state: form.state, shipping_fee: 0 })
      .then((r) => { if (!cancelled) setQuote(r.data); })
      .catch((e) => {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("checkout quote failed", e);
        }
      });
    return () => { cancelled = true; };
  }, [item, form.country, form.state]);

  const update = useCallback((k, v) => setForm((p) => ({ ...p, [k]: v })), []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/storefront/checkout", {
        item_id: item.id,
        ...form,
        state: form.country === "US" ? form.state : undefined,
      });
      nav(`/order/confirmation/${data.id}`, { state: { order: data } });
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "checkout could not be completed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!item) return (
    <div className="bg-ivory min-h-screen"><Navbar /><div className="py-32 text-center editorial-label">loading the piece…</div></div>
  );

  if (item.sold) return (
    <div className="bg-ivory min-h-screen">
      <Navbar />
      <div className="max-w-2xl mx-auto py-24 px-8 text-center">
        <p className="editorial-label">already acquired</p>
        <h1 className="font-serif text-5xl lowercase text-charcoal mt-3 mb-6">this piece found its story.</h1>
        <Link to="/shop" className="bg-brass hover:bg-espresso text-ivory px-8 py-3 inline-block text-xs tracking-[0.25em] uppercase">explore the rest of the edit</Link>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="bg-ivory min-h-screen" data-testid="checkout-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-8 lg:px-16 py-12">
        <Link to={`/product/${item.id}`} className="editorial-label hover:text-brass flex items-center gap-2 mb-8"><ArrowLeft className="w-3 h-3" /> back to the piece</Link>

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left — form */}
          <div className="lg:col-span-7">
            <span className="editorial-label">acquire · checkout</span>
            <h1 className="font-serif text-5xl lowercase text-charcoal mt-3 mb-2">a quiet exchange.</h1>
            <p className="text-espresso mb-10 leading-relaxed">we&apos;ll wrap your piece in the atelier and dispatch within 48 hours. payment is processed in {quote?.currency || settings?.currency_code || "USD"}.</p>

            <form onSubmit={submit} className="space-y-8" data-testid="checkout-form">
              <fieldset>
                <legend className="editorial-label mb-4">contact</legend>
                <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
                  <Field testid="co-name" label="full name" value={form.buyer_name} onChange={(v) => update("buyer_name", v)} required />
                  <Field testid="co-email" label="email" type="email" value={form.buyer_email} onChange={(v) => update("buyer_email", v)} required />
                  <Field testid="co-phone" label="phone (optional)" value={form.buyer_phone} onChange={(v) => update("buyer_phone", v)} />
                </div>
              </fieldset>

              <fieldset>
                <legend className="editorial-label mb-4">ship to</legend>
                <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
                  <div className="md:col-span-2"><Field testid="co-address" label="address" value={form.address_line} onChange={(v) => update("address_line", v)} required /></div>
                  <Field testid="co-city" label="city" value={form.city} onChange={(v) => update("city", v)} required />
                  <Field testid="co-postal" label="postal code" value={form.postal_code} onChange={(v) => update("postal_code", v)} required />
                  <div>
                    <label className="editorial-label">country</label>
                    <select value={form.country} onChange={(e) => update("country", e.target.value)} data-testid="co-country" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal">
                      {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </div>
                  {form.country === "US" && (
                    <div>
                      <label className="editorial-label">state</label>
                      <select value={form.state} onChange={(e) => update("state", e.target.value)} data-testid="co-state" required className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal">
                        <option value="">choose…</option>
                        {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              </fieldset>

              <fieldset>
                <legend className="editorial-label mb-4">notes (optional)</legend>
                <textarea data-testid="co-notes" value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} placeholder="a kind word, gift wrap, etc." className="w-full bg-transparent border-b border-taupe py-2 focus:outline-none focus:border-brass text-charcoal" />
              </fieldset>

              <button type="submit" disabled={submitting} data-testid="co-submit" className="w-full bg-brass hover:bg-espresso text-ivory py-4 text-sm tracking-[0.25em] uppercase transition-colors disabled:opacity-50 flex items-center justify-center gap-3">
                <Lock className="w-4 h-4" /> {submitting ? "processing…" : `acquire · ${formatMoney(quote?.total ?? item.sale_price, quote?.currency)}`}
              </button>
              <p className="text-[0.65rem] text-taupe text-center tracking-widest uppercase">
                {settings?.stripe_enabled ? "secured by stripe" : "mock checkout · no real charge until stripe keys are configured"}
              </p>
            </form>
          </div>

          {/* Right — summary */}
          <aside className="lg:col-span-5 lg:sticky lg:top-8 self-start">
            <div className="bg-linen border border-taupe/50 p-8" data-testid="checkout-summary">
              <p className="editorial-label">your piece</p>
              <div className="flex gap-4 mt-4 mb-6">
                <div className="w-24 h-32 bg-ivory border border-taupe/40 overflow-hidden flex-shrink-0">
                  {item.photos?.[0] ? <img src={fileUrl(item.photos[0]) + "?size=medium"} alt={item.title} className="w-full h-full object-cover" /> : null}
                </div>
                <div>
                  <p className="editorial-label">{item.brand || "—"}</p>
                  <h3 className="font-serif text-xl lowercase text-charcoal mt-1">{item.title}</h3>
                  {item.color && <p className="text-xs text-espresso mt-2">{item.color.toLowerCase()}</p>}
                </div>
              </div>

              <div className="border-t border-taupe/40 pt-4 space-y-2">
                <Row label="subtotal" value={formatMoney(quote?.subtotal ?? item.sale_price, quote?.currency)} />
                <Row label="shipping" value={formatMoney(quote?.shipping_fee ?? 0, quote?.currency)} />
                <Row label={`${quote?.tax_label || "tax"}${quote?.tax_rate ? ` · ${quote.tax_rate}%` : ""}`} value={formatMoney(quote?.tax_amount ?? 0, quote?.currency)} />
                <div className="border-t border-taupe/40 pt-3 mt-3">
                  <Row label="total" value={formatMoney(quote?.total ?? item.sale_price, quote?.currency)} bold accent />
                </div>
              </div>

              <p className="editorial-label mt-8">curated with intention.</p>
              <p className="text-[0.65rem] text-taupe mt-2 leading-relaxed tracking-wider">
                tax is calculated based on your shipping destination and the boutique&apos;s tax residency ({settings?.tax_residency || "US"}).
              </p>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Field({ testid, label, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="editorial-label">{label}</label>
      <input data-testid={testid} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" />
    </div>
  );
}

function Row({ label, value, bold, accent }) {
  return (
    <div className={`flex justify-between ${bold ? "font-medium text-base" : "text-sm"} ${accent ? "text-brass" : "text-charcoal"}`}>
      <span className="lowercase">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
